/**
 * useMediaBrowser
 *
 * Shared hook for the MediaBrowser component. Manages search (debounced), sort,
 * filter, view mode, folder navigation, and selection state.
 *
 * Folder navigation uses GET /documents?parent=<id> (the same pipeline as the
 * root listing) so access-control is applied correctly for owned media files.
 * A separate GET /document?id=<id> call fetches only the folder name for the
 * breadcrumb header.
 *
 * Usage:
 *   const browser = useMediaBrowser({ mode: 'folder' })
 *   <MediaBrowser browser={browser} context="standalone" />
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteDocuments, useQueryDocument } from '@/services/folders/queries'
import type { DocumentInterface } from '@/services/folders/types'

export type SortOption = 'created_desc' | 'created_asc' | 'updated_desc' | 'updated_asc' | 'name_asc'
export type FilterOption = 'all' | 'ai_generated' | 'uploaded'
export type ViewMode = 'icon' | 'comfortable' | 'list' | 'columns'
export type BrowserMode = 'folder' | 'flat'

const SORT_MAP: Record<SortOption, { sort: string; sort_by: string }> = {
    created_desc: { sort: 'createdAt', sort_by: 'desc' },
    created_asc: { sort: 'createdAt', sort_by: 'asc' },
    updated_desc: { sort: 'updatedAt', sort_by: 'desc' },
    updated_asc: { sort: 'updatedAt', sort_by: 'asc' },
    name_asc: { sort: 'name', sort_by: 'asc' },
}

export interface UseMediaBrowserOptions {
    mode?: BrowserMode
    /** Extra query params merged into every request (flat mode: drives filtering). */
    baseQueryParams?: Record<string, string>
    /** localStorage key for persisting view mode between sessions. */
    viewModeStorageKey?: string
}

export interface UseMediaBrowserReturn {
    items: DocumentInterface[]
    isLoading: boolean
    // Search
    searchInput: string
    setSearchInput: (v: string) => void
    // Sort / Filter / View
    sort: SortOption
    setSort: (s: SortOption) => void
    filter: FilterOption
    setFilter: (f: FilterOption) => void
    viewMode: ViewMode
    setViewMode: (m: ViewMode) => void
    // Folder nav
    folderHistory: string[]
    currentFolderId: string | null
    folderName: string | undefined
    enterFolder: (id: string) => void
    goBack: () => void
    // Selection
    selectedIds: Set<string>
    toggleSelect: (id: string) => void
    selectAll: () => void
    clearSelection: () => void
    isSelected: (id: string) => boolean
    // Pagination (infinite)
    fetchNextPage: () => void
    hasNextPage: boolean
    isFetchingNextPage: boolean
    refetch: () => void
}

export function useMediaBrowser(options: UseMediaBrowserOptions = {}): UseMediaBrowserReturn {
    const { mode = 'folder', baseQueryParams = {}, viewModeStorageKey } = options

    // ── Search with 300ms debounce ────────────────────────────────────────────
    const [searchInput, setSearchInputRaw] = useState('')
    const [search, setSearch] = useState('')
    const debounceRef = useRef<ReturnType<typeof setTimeout>>()
    const setSearchInput = useCallback((val: string) => {
        setSearchInputRaw(val)
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => setSearch(val), 300)
    }, [])
    useEffect(() => () => clearTimeout(debounceRef.current), [])

    // ── Sort / Filter ─────────────────────────────────────────────────────────
    const [sort, setSort] = useState<SortOption>('created_desc')
    const [filter, setFilter] = useState<FilterOption>('all')

    // ── View mode (optionally persisted) ─────────────────────────────────────
    const [viewMode, setViewModeState] = useState<ViewMode>(() => {
        if (viewModeStorageKey) {
            return (localStorage.getItem(viewModeStorageKey) as ViewMode) || 'comfortable'
        }
        return 'comfortable'
    })
    const setViewMode = useCallback(
        (m: ViewMode) => {
            setViewModeState(m)
            if (viewModeStorageKey) localStorage.setItem(viewModeStorageKey, m)
        },
        [viewModeStorageKey],
    )

    // ── Folder navigation ─────────────────────────────────────────────────────
    const [folderHistory, setFolderHistory] = useState<string[]>([])
    const currentFolderId = folderHistory[folderHistory.length - 1] ?? null
    const enterFolder = useCallback((id: string) => setFolderHistory(prev => [...prev, id]), [])
    const goBack = useCallback(() => setFolderHistory(prev => prev.slice(0, -1)), [])

    // ── Selection ─────────────────────────────────────────────────────────────
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const toggleSelect = useCallback((id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }, [])
    const clearSelection = useCallback(() => setSelectedIds(new Set()), [])
    const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds])

    // ── Build query params ────────────────────────────────────────────────────
    const { sort: sortField, sort_by } = SORT_MAP[sort]
    const queryParams = useMemo(() => {
        const params: Record<string, string> = {
            ...baseQueryParams,
            sort: sortField,
            sort_by,
        }
        // When inside a folder, scope results to that folder's direct children
        // using the /documents?parent=... API (same pipeline as root, correct
        // access-control).  The old approach (useQueryDocument items field)
        // relied on an overly-restrictive pipeline that hid owned files.
        if (currentFolderId) {
            params.parent = currentFolderId
        }
        if (search) {
            params.search = search
            // Include descendants when searching inside a folder so nested items surface.
            if (currentFolderId) {
                params.include_children = 'true'
            }
        }
        if (filter === 'ai_generated') params['metadata.ai_generated'] = 'true'
        if (filter === 'uploaded') params['metadata.source_system'] = 'upload'
        return params
    }, [baseQueryParams, sortField, sort_by, search, filter, currentFolderId])

    const folderListQuery = useInfiniteDocuments({
        queryParams,
        enabled: mode === 'folder',
        pageSize: 50,
    })

    // Fetch the folder's own document record — used only for the breadcrumb name.
    const { data: folderMeta } = useQueryDocument({
        enabled: mode === 'folder' && !!currentFolderId,
        queryParams: { id: currentFolderId ?? '' },
    })

    const flatQuery = useInfiniteDocuments({
        queryParams,
        enabled: mode === 'flat',
        pageSize: 80,
    })

    // ── Derived items ─────────────────────────────────────────────────────────
    const items: DocumentInterface[] = useMemo(() => {
        const data = mode === 'flat' ? flatQuery.data : folderListQuery.data
        const pages = data?.pages || []
        return pages.flatMap((p: any) => p?.data || [])
    }, [mode, flatQuery.data, folderListQuery.data])

    const isLoading = mode === 'flat' ? flatQuery.isLoading : folderListQuery.isLoading

    const folderName = currentFolderId ? (folderMeta as any)?.name : undefined

    const refetch = useCallback(() => {
        if (mode === 'folder') folderListQuery.refetch()
        else flatQuery.refetch()
    }, [mode, folderListQuery, flatQuery])

    const selectAll = useCallback(() => {
        const imageIds = items.filter(i => !i.isFolder && i.url).map(i => i._id)
        setSelectedIds(new Set(imageIds))
    }, [items])

    return {
        items,
        isLoading,
        searchInput,
        setSearchInput,
        sort,
        setSort,
        filter,
        setFilter,
        viewMode,
        setViewMode,
        folderHistory,
        currentFolderId,
        folderName,
        enterFolder,
        goBack,
        selectedIds,
        toggleSelect,
        selectAll,
        clearSelection,
        isSelected,
        fetchNextPage: () => {
            if (mode === 'folder') folderListQuery.fetchNextPage()
            else flatQuery.fetchNextPage()
        },
        hasNextPage: mode === 'folder' ? !!folderListQuery.hasNextPage : !!flatQuery.hasNextPage,
        isFetchingNextPage:
            mode === 'folder' ? !!folderListQuery.isFetchingNextPage : !!flatQuery.isFetchingNextPage,
        refetch,
    }
}
